import { TOOLBOX_DOWNLOAD_PAGE_PATH } from "../updates.js";
// ---------------------------------------------------------------------------
// Limits (the same as toolboxNotificationSchema in bridge.ts)
// ---------------------------------------------------------------------------
/** Longest notification title the app may show (`toolboxNotificationSchema`). */
export const GROOMING_QUEUE_NOTIFICATION_TITLE_MAX = 80;
/** Longest notification body the app may show (`toolboxNotificationSchema`). */
export const GROOMING_QUEUE_NOTIFICATION_BODY_MAX = 240;
// ---------------------------------------------------------------------------
// Fix it actions and setup codes
// ---------------------------------------------------------------------------
/**
 * What "Fix it" on a paused queue does (01-roles.md section 9.6: reuse
 * PKG-PM-H's actions, no new contract number):
 * - `claude-sign-in`: open Claude's sign-in from the Queue page and show the result there;
 * - `open-setup-step`: open that step of Setup (see `setupStep`), which installs or reconnects, then come back to the Queue;
 * - `repo-sign-in`: run "Sign in to the repos" from the Queue page;
 * - `check-connection`: check the connection now instead of waiting for the next minute;
 * - `get-update`: download the latest Toolbox (the queue is kept and starts again after the reinstall).
 *
 * A successful fix starts the queue again by itself.
 */
export const GROOMING_QUEUE_FIX_ACTIONS = [
    "claude-sign-in",
    "open-setup-step",
    "repo-sign-in",
    "check-connection",
    "get-update",
];
/**
 * The engine codes that pause the whole queue for setup: every code whose
 * `GROOMING_ENGINE_ERRORS` effect is `batch` and that is not the usage limit.
 * The contract tests derive the same list from `GROOMING_ENGINE_ERRORS`, so a
 * new batch code without wording fails them.
 */
export const GROOMING_QUEUE_SETUP_CODES = [
    "CLAUDE_NOT_SIGNED_IN",
    "CLAUDE_NOT_INSTALLED",
    "PLUGIN_MISSING",
    "MCP_NOT_CONNECTED",
    "ADO_PAT_INVALID",
    "NETWORK",
    "REPO_AUTH_REQUIRED",
    "GIT_NOT_INSTALLED",
];
/** True for an engine code that pauses the whole queue for setup. */
export function isGroomingQueueSetupCode(code) {
    return typeof code === "string" && GROOMING_QUEUE_SETUP_CODES.includes(code);
}
const INSTALL_IT_BODY = "Open the Toolbox and click Fix it to install it.";
const UPDATE_COPY = {
    banner: "Paused: part of the Toolbox that Claude needs is missing or out of date. Getting the latest version fixes it.",
    bannerFor: () => UPDATE_COPY.banner,
    fixAction: "get-update",
    fixHint: "Downloads the latest version · the queue is kept and starts again after the reinstall",
    notificationTitle: "Grooming paused: the Toolbox needs an update",
    notificationBody: () => "Click to download the latest version.",
    notificationTargetPath: TOOLBOX_DOWNLOAD_PAGE_PATH,
};
const REPO_FALLBACK = "the project's code";
/**
 * The host's name as a person reads it: "GitHub" for `"github"`, otherwise
 * "Azure DevOps" (absent, `"ado"`, or a value this version does not know).
 * Pure (epic PI-SPEC-SW-E318).
 */
export function repoHostLabel(host) {
    return host === "github" ? "GitHub" : "Azure DevOps";
}
/**
 * Where a clone URL points: `"github"` when its host is github.com (or
 * www.github.com, any letter case), otherwise `"ado"`. Lets a Toolbox
 * classify a repository from an older SpecTree's config read, which has a
 * `cloneUrl` but no `host`. Pure; an unreadable URL is `"ado"`.
 */
export function repoHostFromCloneUrl(cloneUrl) {
    if (typeof cloneUrl !== "string")
        return "ado";
    const match = /^[a-z][a-z0-9+.-]*:\/\/(?:[^/?#@]*@)?([^/?#:]+)/i.exec(cloneUrl.trim());
    const hostname = match?.[1]?.toLowerCase();
    return hostname === "github.com" || hostname === "www.github.com" ? "github" : "ado";
}
/**
 * Frame B3's wording for every setup code. A plugin or server problem
 * (`PLUGIN_MISSING`, `MCP_NOT_CONNECTED`) reads "the Toolbox needs an
 * update": since PKG-PM-H the app carries its own Claude add-on and SpecTree
 * server, so a newer app is the only fix.
 */
export const GROOMING_QUEUE_SETUP_COPY = {
    CLAUDE_NOT_SIGNED_IN: {
        banner: "Paused: Claude needs you to sign in again.",
        bannerFor: () => "Paused: Claude needs you to sign in again.",
        fixAction: "claude-sign-in",
        fixHint: "Opens Claude's sign-in, result shown here · starts again by itself",
        notificationTitle: "Grooming paused: sign in to Claude",
        notificationBody: (ctx) => {
            const tail = "Sign in and the rest carry on by themselves.";
            const done = count(ctx.done);
            const total = count(ctx.total);
            if (done === null || total === null || total === 0)
                return tail;
            return `${String(done)} of ${String(total)} ${total === 1 ? "brief is" : "briefs are"} done. ${tail}`;
        },
    },
    CLAUDE_NOT_INSTALLED: {
        banner: "Paused: Claude Code is missing from this computer.",
        bannerFor: () => "Paused: Claude Code is missing from this computer.",
        fixAction: "open-setup-step",
        setupStep: "claude-code",
        fixHint: "Opens Setup step 3, which installs it · back here, starts again",
        notificationTitle: "Grooming paused: Claude Code is missing",
        notificationBody: () => INSTALL_IT_BODY,
    },
    PLUGIN_MISSING: UPDATE_COPY,
    MCP_NOT_CONNECTED: UPDATE_COPY,
    ADO_PAT_INVALID: {
        banner: "Paused: your Azure DevOps token has run out or no longer works.",
        bannerFor: () => "Paused: your Azure DevOps token has run out or no longer works.",
        fixAction: "open-setup-step",
        setupStep: "azure-devops",
        fixHint: "Opens Setup step 2, Connect Azure DevOps, tested on save · back here, starts again",
        notificationTitle: "Grooming paused: renew your Azure DevOps token",
        notificationBody: () => "It takes about 2 minutes. The rest then carry on by themselves.",
    },
    NETWORK: {
        banner: "Paused: this computer can't reach SpecTree or Azure DevOps. It checks again every minute.",
        bannerFor: () => "Paused: this computer can't reach SpecTree or Azure DevOps. It checks again every minute.",
        fixAction: "check-connection",
        fixHint: "Checks the connection now · starts again by itself when it's back",
        notificationTitle: "Grooming paused: no connection",
        notificationBody: () => "It carries on by itself when the connection is back.",
    },
    REPO_AUTH_REQUIRED: {
        banner: `Paused: Azure DevOps wants you to sign in again to read ${REPO_FALLBACK}.`,
        bannerFor: (ctx) => {
            const repo = typeof ctx.repo === "string" && ctx.repo.trim().length > 0 ? ctx.repo.trim() : REPO_FALLBACK;
            return `Paused: ${repoHostLabel(ctx.host)} wants you to sign in again to read ${repo}.`;
        },
        fixAction: "repo-sign-in",
        fixHint: "Runs \"Sign in to the repos\" here · starts again by itself",
        notificationTitle: "Grooming paused: sign in to the project's code",
        notificationBody: (ctx) => `${repoHostLabel(ctx.host)} asked for your sign-in again.`,
    },
    GIT_NOT_INSTALLED: {
        banner: "Paused: Git, which reads the project's code, is missing from this computer.",
        bannerFor: () => "Paused: Git, which reads the project's code, is missing from this computer.",
        fixAction: "open-setup-step",
        setupStep: "git",
        fixHint: "Opens Setup step 4, which installs it · back here, starts again",
        notificationTitle: "Grooming paused: Git is missing",
        notificationBody: () => INSTALL_IT_BODY,
    },
};
/** Banner for a `setup` pause whose code this contract version does not know, or that names none. */
export const GROOMING_QUEUE_SETUP_FALLBACK_BANNER = "Paused: something on this computer needs fixing before the runs can go on.";
/** Notification for a `setup` pause whose code this contract version does not know, or that names none. */
export const GROOMING_QUEUE_SETUP_FALLBACK_NOTIFICATION = {
    title: "Grooming paused",
    body: "Open the Toolbox to see what it needs.",
};
// ---------------------------------------------------------------------------
// Pause reasons (frame B3, "reason of its own" rows)
// ---------------------------------------------------------------------------
/** Title of the notification sent when the queue starts again by itself (after a wake, a fix or the connection coming back). */
export const GROOMING_QUEUE_PICKED_UP_TITLE = "Grooming picked up again";
/** Title of the notification sent when the queue has nothing left to run. */
export const GROOMING_QUEUE_FINISHED_TITLE = "Grooming queue finished";
// ---------------------------------------------------------------------------
// Items taken off the agenda (grooming contract 4, epic PI-SPEC-SW-E323)
// ---------------------------------------------------------------------------
/**
 * The line a queue entry shows when its item was taken off the agenda while
 * the run had it queued: the API answered the item report with
 * `GROOMING_BATCH_ITEM_REMOVED`. A skip, not a failure: it never counts as
 * "couldn't finish" and never pauses the queue.
 */
export const GROOMING_QUEUE_ITEM_REMOVED_TEXT = "Taken off the agenda, skipped";
/**
 * The banner sentence for a paused queue:
 * - `sleep`: "Picked up again after this computer woke at 7:58 AM. 5 briefs still to do." (an info line; the queue starts again by itself on wake);
 * - `usage-limit`: "Paused before your Claude limit. Starts again by itself at about 2:30 PM.";
 * - `launch`: "5 items waiting from last time." (with Resume), or, while signed out of SpecTree, "Signed out of SpecTree. 6 items wait until you sign in again.";
 * - `user`: "You stopped the run. 5 items waiting." (with Resume);
 * - `setup`: the code's {@link GroomingQueueSetupCopy.bannerFor}, never `pause.fix` (an older app sends its own command texts there).
 */
export function queuePauseBanner(pause, ctx = {}) {
    switch (pause.reason) {
        case "sleep":
            return queuePickedUpBanner(ctx);
        case "usage-limit": {
            const when = pause.resumeAt ? formatLocalClock(pause.resumeAt, { ...ctx.clock, about: true }) : null;
            return when
                ? `Paused before your Claude limit. Starts again by itself ${when}.`
                : "Paused before your Claude limit. Starts again by itself.";
        }
        case "launch":
            return ctx.signedOut ? queueSignedOutBanner(ctx.left) : withCount(ctx.left, (n) => `${String(n)} ${plural(n, "item", "items")} waiting from last time.`, "Items waiting from last time.");
        case "user":
            return withCount(ctx.left, (n) => `You stopped the run. ${String(n)} ${plural(n, "item", "items")} waiting.`, "You stopped the run.");
        case "setup":
            return isGroomingQueueSetupCode(pause.errorCode)
                ? GROOMING_QUEUE_SETUP_COPY[pause.errorCode].bannerFor(ctx)
                : GROOMING_QUEUE_SETUP_FALLBACK_BANNER;
        default:
            return GROOMING_QUEUE_SETUP_FALLBACK_BANNER;
    }
}
/** "Picked up again after this computer woke at 7:58 AM. 5 briefs still to do." */
export function queuePickedUpBanner(ctx) {
    const when = ctx.wokeAt ? formatLocalClock(ctx.wokeAt, ctx.clock) : null;
    const head = when ? `Picked up again after this computer woke ${when}.` : "Picked up again after this computer woke.";
    const n = count(ctx.left);
    return n === null ? head : `${head} ${String(n)} ${plural(n, "brief", "briefs")} still to do.`;
}
/** "Signed out of SpecTree. 6 items wait until you sign in again." (frame B5; the queue then shows a `launch` pause after sign-in). */
export function queueSignedOutBanner(left) {
    const n = count(left);
    if (n === null)
        return "Signed out of SpecTree. The queue waits until you sign in again.";
    return `Signed out of SpecTree. ${String(n)} ${plural(n, "item waits", "items wait")} until you sign in again.`;
}
// ---------------------------------------------------------------------------
// Sign out while Claude is working (frame B5, UX-PM-19, feature PI-SPEC-SW-1485)
// ---------------------------------------------------------------------------
/** The question when Sign Out would stop runs (frame B5). */
export const SIGN_OUT_CONFIRM_TITLE = "Sign out while Claude is working?";
/** The safe answer, and the default (frame B5). */
export const SIGN_OUT_KEEP_WORKING = "Keep working";
/** The counts the sign-out question names, from the queue's lists. */
export function signOutCounts(state) {
    return {
        running: state.running.length,
        waiting: state.upNext.length,
        done: state.completed.filter((e) => e.state === "briefed" || e.state === "done").length,
    };
}
/** The sign-out question's words (frame B5), plural-aware ("1 brief"). */
export function signOutConfirmCopy(counts) {
    const running = count(counts.running) ?? 0;
    const waiting = count(counts.waiting) ?? 0;
    const done = count(counts.done) ?? 0;
    const one = running === 1;
    const stops = [
        { text: "Signing out stops the " },
        { text: `${String(running)} ${plural(running, "brief", "briefs")}`, strong: true },
        { text: ` Claude is writing now. ${one ? "It goes" : "They go"} back to the front of the queue` },
    ];
    if (waiting > 0) {
        stops.push({ text: " with the " }, { text: `${String(waiting)} waiting`, strong: true }, { text: `, and all ${String(running + waiting)} wait until you sign in again.` });
    }
    else {
        stops.push({ text: ` and ${one ? "waits" : "wait"} until you sign in again.` });
    }
    const stay = "Your Claude sign-in and your copies of the code stay on this computer.";
    const kept = done > 0
        ? `The ${String(done)} ${plural(done, "brief", "briefs")} already finished ${plural(done, "is", "are")} kept. ${stay}`
        : stay;
    return {
        title: SIGN_OUT_CONFIRM_TITLE,
        stops,
        kept,
        keepLabel: SIGN_OUT_KEEP_WORKING,
        confirmLabel: `Sign out and stop ${String(running)} ${plural(running, "brief", "briefs")}`,
    };
}
/** The sentence parts as plain text (the native message box has no bold). */
export function signOutTextPlain(parts) {
    return parts.map((p) => p.text).join("");
}
/**
 * The notification for a pause, or null when it raises none. Only pauses
 * that happen while the person is away notify: `usage-limit` and every
 * `setup` code. `launch` and `user` never notify (the window is in front, or
 * the person chose it). `sleep` notifies on wake, not on sleep: send
 * {@link queuePickedUpNotification} then.
 *
 * Titles fit {@link GROOMING_QUEUE_NOTIFICATION_TITLE_MAX} and bodies
 * {@link GROOMING_QUEUE_NOTIFICATION_BODY_MAX}; an over-long session name is
 * cut.
 */
export function queuePauseNotification(pause, ctx = {}) {
    switch (pause.reason) {
        case "usage-limit": {
            const parts = pause.resumeAt ? formatLocalClockParts(pause.resumeAt, ctx.clock) : null;
            const until = parts ? `until ${parts.day ? `${parts.day} at ` : ""}about ${parts.time}` : "before your Claude limit";
            const n = count(ctx.left);
            const rest = n === null || n === 0
                ? "The rest carry on by themselves."
                : n === 1
                    ? "The other brief carries on by itself."
                    : `The other ${String(n)} briefs carry on by themselves.`;
            return fitNotification({ title: `Grooming paused ${until}`, body: `You're close to your Claude limit. ${rest}` });
        }
        case "setup": {
            if (!isGroomingQueueSetupCode(pause.errorCode))
                return { ...GROOMING_QUEUE_SETUP_FALLBACK_NOTIFICATION };
            const copy = GROOMING_QUEUE_SETUP_COPY[pause.errorCode];
            return fitNotification({
                title: copy.notificationTitle,
                body: copy.notificationBody(ctx),
                ...(copy.notificationTargetPath ? { targetPath: copy.notificationTargetPath } : {}),
            });
        }
        default:
            return null;
    }
}
/**
 * The notification sent when the queue starts again by itself: "Grooming
 * picked up again" / "The computer slept at 4:40 PM. 5 briefs for Monday
 * still to do." Without `sleptAt` (after a fix or the connection coming back)
 * the first sentence is left out; without `session`, " for Monday" is.
 */
export function queuePickedUpNotification(ctx = {}) {
    const slept = ctx.sleptAt ? formatLocalClock(ctx.sleptAt, ctx.clock) : null;
    const n = count(ctx.left);
    const session = typeof ctx.session === "string" && ctx.session.trim().length > 0 ? ctx.session.trim() : null;
    const head = slept ? `The computer slept ${slept}.` : "";
    const build = (withSession) => {
        const forSession = withSession && session ? ` for ${session}` : "";
        const tail = n === null ? (forSession ? `The briefs${forSession} carry on.` : "The briefs carry on.") : `${String(n)} ${plural(n, "brief", "briefs")}${forSession} still to do.`;
        return [head, tail].filter((part) => part.length > 0).join(" ");
    };
    let body = build(true);
    if (body.length > GROOMING_QUEUE_NOTIFICATION_BODY_MAX)
        body = build(false);
    return fitNotification({ title: GROOMING_QUEUE_PICKED_UP_TITLE, body });
}
/** The "Grooming queue finished" body: "7 briefs ready", plus " · 1 couldn't finish" when any failed. */
export function queueFinishedNotificationBody(briefed, failed = 0) {
    const n = count(briefed) ?? 0;
    const f = count(failed) ?? 0;
    const ready = `${String(n)} ${plural(n, "brief", "briefs")} ready`;
    return f > 0 ? `${ready} · ${String(f)} couldn't finish` : ready;
}
/**
 * The usage line: "62% of your 5-hour allowance · 31% of this week's". A
 * window Claude did not report is left out; null when neither is known.
 */
export function queueUsageText(usage) {
    const five = usage?.fiveHour ? percent(usage.fiveHour.utilization) : null;
    const week = usage?.sevenDay ? percent(usage.sevenDay.utilization) : null;
    if (five !== null && week !== null)
        return `${String(five)}% of your 5-hour allowance · ${String(week)}% of this week's`;
    if (five !== null)
        return `${String(five)}% of your 5-hour allowance`;
    if (week !== null)
        return `${String(week)}% of this week's allowance`;
    return null;
}
/** {@link formatLocalClock} split into its day and time, or null for an unreadable time. */
export function formatLocalClockParts(iso, options = {}) {
    if (typeof iso !== "string" || iso.trim().length === 0)
        return null;
    const at = Date.parse(iso);
    if (Number.isNaN(at))
        return null;
    const now = options.now === undefined ? Date.now() : typeof options.now === "number" ? options.now : options.now.getTime();
    if (Number.isNaN(now))
        return null;
    const timeZone = knownTimeZone(options.timeZone);
    const time = clean(new Intl.DateTimeFormat("en-US", { hour: "numeric", minute: "2-digit", hour12: true, timeZone }).format(at));
    const days = calendarDay(at, timeZone) - calendarDay(now, timeZone);
    let day;
    if (days === 0)
        day = null;
    else if (days === -1)
        day = "yesterday";
    else if (days === 1)
        day = "tomorrow";
    else if (Math.abs(days) <= 6)
        day = clean(new Intl.DateTimeFormat("en-US", { weekday: "short", timeZone }).format(at));
    else
        day = clean(new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", timeZone }).format(at));
    return { day, time };
}
/**
 * An ISO 8601 time in the person's own words, never a raw timestamp:
 * "at 4:12 PM" today, "yesterday at 4:40 PM", "tomorrow at 9:05 AM",
 * "Mon at 9:05 AM" within six days, "Oct 28 at 9:05 AM" beyond. With
 * `about`: "at about 2:30 PM". Null for an unreadable time.
 */
export function formatLocalClock(iso, options = {}) {
    const parts = formatLocalClockParts(iso, options);
    if (!parts)
        return null;
    const at = `at ${options.about ? "about " : ""}${parts.time}`;
    return parts.day ? `${parts.day} ${at}` : at;
}
// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function count(value) {
    return typeof value === "number" && Number.isFinite(value) && value >= 0 ? Math.floor(value) : null;
}
function plural(n, one, many) {
    return n === 1 ? one : many;
}
function withCount(value, sentence, fallback) {
    const n = count(value);
    return n === null ? fallback : sentence(n);
}
function percent(utilization) {
    return Math.round(Math.min(1, Math.max(0, utilization)) * 100);
}
function cut(text, max) {
    return text.length <= max ? text : `${text.slice(0, max - 1).trimEnd()}…`;
}
function fitNotification(notification) {
    return {
        ...notification,
        title: cut(notification.title, GROOMING_QUEUE_NOTIFICATION_TITLE_MAX),
        body: cut(notification.body, GROOMING_QUEUE_NOTIFICATION_BODY_MAX),
    };
}
/** Intl output with plain spaces (ICU puts a narrow no-break space before AM/PM). */
function clean(text) {
    return text.replace(/[\u202f\u00a0]/g, " ");
}
function knownTimeZone(timeZone) {
    if (!timeZone)
        return undefined;
    try {
        new Intl.DateTimeFormat("en-US", { timeZone });
        return timeZone;
    }
    catch {
        return undefined;
    }
}
/** Days since 1970-01-01 of the calendar date `at` falls on in `timeZone`. */
function calendarDay(at, timeZone) {
    const parts = new Intl.DateTimeFormat("en-US", { year: "numeric", month: "numeric", day: "numeric", timeZone }).formatToParts(at);
    const get = (type) => Number(parts.find((part) => part.type === type)?.value);
    return Math.round(Date.UTC(get("year"), get("month") - 1, get("day")) / 86_400_000);
}
//# sourceMappingURL=queue-copy.js.map